import React, { useMemo } from "react";

/**
 * Itemised breakdown of a member's minimum spend.
 *
 * The single total on the minimum-spend page is not reviewable: when a figure
 * looks wrong there is no way to tell whether the cause is a transaction that
 * should not count, a refund that was not deducted, or a missing Clover order.
 * That is how members ended up being shown figures nobody could defend. This
 * shows every transaction in the period, what it contributed, and — for anything
 * that contributed nothing — why.
 *
 * IMPORTANT: the classification below MUST mirror calculateTotals in
 * CF-StripeCloudFunctions/utils/helper.js. If the two ever disagree, this report
 * becomes confidently wrong, which is worse than showing nothing. Rather than
 * trust that they stay in step, the component recomputes the total from its own
 * classification and compares it against the API's finalTotal, surfacing a
 * warning when they differ. Treat that warning as "the report is untrustworthy",
 * not as a rounding curiosity.
 */

const CENTS = (n) => (Number(n || 0) / 100);
const money = (n) => `$${CENTS(n).toFixed(2)}`;

export const EXCLUSION = {
    DUES: "Membership dues - excluded from minimum spend",
    SHORTFALL_CHARGE: "End-of-year minimum-spend charge - does not count toward the next year",
    REFUND_FLAG: "Marked refunded on the invoice",
    FULLY_REFUNDED: "Refunded in full",
    NOT_PAID: "Invoice never paid",
};

/**
 * Classify one transaction exactly as the backend does.
 * Returns { counted, gross, refunded, excludedReason }  — all in cents.
 */
export function classifyTransaction(txn) {
    // --- Clover: counted at its order total, no invoice concepts apply.
    if (txn.isFrom === "CLOVER") {
        const gross = Number(txn.total || 0);
        return { counted: gross, gross, refunded: 0, excludedReason: null, source: "Clover" };
    }

    // --- Local vehicle bookings: only the portion not put on a card.
    if (txn.isFrom === "LOCAL") {
        const metadata = txn.metadata || {};
        if (metadata.isPaymentFrom === "Vehicle_Booking") {
            let chargeCard = 0;
            try {
                chargeCard = (metadata.otherInfo ? JSON.parse(metadata.otherInfo) : {}).chargeCard || 0;
            } catch (e) {
                chargeCard = 0;
            }
            const gross = Number(txn.amount || 0);
            return {
                counted: Math.max(0, gross - chargeCard),
                gross,
                refunded: 0,
                excludedReason: null,
                source: "Vehicle booking",
            };
        }
        return { counted: 0, gross: 0, refunded: 0, excludedReason: "Not a qualifying local charge", source: "Local" };
    }

    // --- Stripe.
    const invoice = txn.invoice || null;
    const charge = txn.charge || null;
    const source = "Stripe";

    const billingReason = (invoice && invoice.billing_reason) || "";
    if (billingReason.startsWith("subscription")) {
        const gross = invoice ? Number(invoice.amount_paid ?? invoice.total ?? 0) : 0;
        return { counted: 0, gross, refunded: 0, excludedReason: EXCLUSION.DUES, source };
    }

    // The January shortfall charge relates to the year that just closed, so it
    // must not count toward the year it lands in. Mirrors calculateTotals.
    if (invoice && invoice.metadata && invoice.metadata.isPaymentFrom === "Minimum_Spend_Adjustment") {
        const gross = Number(invoice.amount_paid ?? invoice.total ?? 0);
        return { counted: 0, gross, refunded: 0, excludedReason: EXCLUSION.SHORTFALL_CHARGE, source };
    }

    if (invoice && invoice.metadata && invoice.metadata.refunded === "true") {
        const gross = Number(invoice.amount_paid ?? invoice.total ?? 0);
        return { counted: 0, gross, refunded: gross, excludedReason: EXCLUSION.REFUND_FLAG, source };
    }

    let gross = 0;
    if (invoice) {
        gross = typeof invoice.amount_paid === "number" ? invoice.amount_paid : Number(invoice.total || 0);
    } else if (charge) {
        gross = Number(charge.amount || 0);
    }

    const refunded = charge
        ? Number(charge.amount_refunded || 0)
        : (invoice && invoice.charge && typeof invoice.charge === "object"
            ? Number(invoice.charge.amount_refunded || 0)
            : 0);

    const counted = Math.max(0, gross - refunded);
    let excludedReason = null;
    if (counted === 0) {
        excludedReason = refunded > 0 ? EXCLUSION.FULLY_REFUNDED : EXCLUSION.NOT_PAID;
    }
    return { counted, gross, refunded, excludedReason, source };
}

function describe(txn) {
    if (txn.isFrom === "CLOVER") {
        return txn.note ? `Clover order - ${txn.note}` : "Clover order";
    }
    const invoice = txn.invoice || {};
    const charge = txn.charge || {};
    return invoice.description || charge.description || invoice.number || charge.id || "(no description)";
}

function whenOf(txn) {
    const t =
        (txn.invoice && txn.invoice.created) ||
        (txn.charge && txn.charge.created) ||
        txn.createdTime ||
        txn.createdAt ||
        null;
    if (!t) return "";
    const ms = String(t).length > 11 ? Number(t) : Number(t) * 1000;
    const d = new Date(ms);
    return isNaN(d.getTime()) ? "" : d.toLocaleDateString();
}

function referenceOf(txn) {
    const invoice = txn.invoice || {};
    const charge = txn.charge || {};
    return invoice.number || invoice.id || charge.id || txn.id || "";
}

export default function MinimumSpendBreakdown({
    transactions = [],
    apiTotal = null,          // final.finalTotal from the API, in cents
    annualRequirement = 0,    // dollars
    periodStart = null,
    periodEnd = null,
    memberName = "",
    memberNumber = "",
}) {
    const rows = useMemo(
        () =>
            (transactions || []).map((txn) => {
                const c = classifyTransaction(txn);
                return {
                    when: whenOf(txn),
                    reference: referenceOf(txn),
                    description: describe(txn),
                    ...c,
                };
            }),
        [transactions]
    );

    const countedTotal = rows.reduce((s, r) => s + r.counted, 0);
    const excludedTotal = rows.reduce((s, r) => s + (r.excludedReason ? r.gross : 0), 0);
    const refundedTotal = rows.reduce((s, r) => s + r.refunded, 0);
    const requiredCents = Math.round(Number(annualRequirement || 0) * 100);
    const remaining = Math.max(0, requiredCents - countedTotal);

    // If our classification and the backend disagree, the report cannot be
    // trusted for reconciliation — say so loudly rather than showing a number
    // that looks authoritative.
    const drift = apiTotal === null ? 0 : Math.abs(Number(apiTotal) - countedTotal);
    const hasDrift = apiTotal !== null && drift > 1; // 1 cent tolerance

    const exportCsv = () => {
        const header = ["Date", "Source", "Reference", "Description", "Gross", "Refunded", "Counted", "Excluded because"];
        const body = rows.map((r) => [
            r.when,
            r.source,
            r.reference,
            (r.description || "").replace(/"/g, '""'),
            CENTS(r.gross).toFixed(2),
            CENTS(r.refunded).toFixed(2),
            CENTS(r.counted).toFixed(2),
            r.excludedReason || "",
        ]);
        const csv = [header, ...body]
            .map((line) => line.map((cell) => `"${String(cell ?? "")}"`).join(","))
            .join("\n");
        const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
        const a = document.createElement("a");
        a.href = url;
        a.download = `minimum-spend-${memberNumber || "member"}-${periodStart || ""}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    return (
        <div className="minimum-spend-breakdown">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <h3 style={{ margin: 0 }}>
                    Spend breakdown{memberName ? ` - ${memberName}` : ""}
                    {memberNumber ? ` (#${memberNumber})` : ""}
                </h3>
                <button className="fillButton" onClick={exportCsv} disabled={rows.length === 0}>
                    EXPORT CSV
                </button>
            </div>

            {periodStart && periodEnd ? (
                <p style={{ marginTop: 0, color: "#666" }}>
                    Period measured: <strong>{periodStart}</strong> to <strong>{periodEnd}</strong>
                </p>
            ) : null}

            {hasDrift ? (
                <div
                    style={{
                        border: "1px solid #b00",
                        background: "#fff4f4",
                        padding: "10px 14px",
                        borderRadius: 6,
                        marginBottom: 14,
                    }}
                >
                    <strong>This breakdown does not reconcile.</strong> The itemised lines add up to{" "}
                    {money(countedTotal)} but the API reports {money(apiTotal)} - a difference of {money(drift)}.
                    That means this page and the server are applying different rules, so do not rely on either
                    figure until it is investigated.
                </div>
            ) : null}

            <table className="table" style={{ width: "100%", marginBottom: 18 }}>
                <tbody>
                    <tr>
                        <td>Annual minimum</td>
                        <td style={{ textAlign: "right" }}>{money(requiredCents)}</td>
                    </tr>
                    <tr>
                        <td>Counted toward minimum</td>
                        <td style={{ textAlign: "right" }}><strong>{money(countedTotal)}</strong></td>
                    </tr>
                    <tr>
                        <td>Remaining</td>
                        <td style={{ textAlign: "right" }}>{money(remaining)}</td>
                    </tr>
                    <tr>
                        <td style={{ color: "#666" }}>Excluded (dues, refunds, unpaid)</td>
                        <td style={{ textAlign: "right", color: "#666" }}>{money(excludedTotal)}</td>
                    </tr>
                    <tr>
                        <td style={{ color: "#666" }}>Refunded within the period</td>
                        <td style={{ textAlign: "right", color: "#666" }}>{money(refundedTotal)}</td>
                    </tr>
                </tbody>
            </table>

            <table className="table" style={{ width: "100%" }}>
                <thead>
                    <tr>
                        <th>Date</th>
                        <th>Source</th>
                        <th>Reference</th>
                        <th>Description</th>
                        <th style={{ textAlign: "right" }}>Gross</th>
                        <th style={{ textAlign: "right" }}>Refunded</th>
                        <th style={{ textAlign: "right" }}>Counted</th>
                        <th>Excluded because</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.length === 0 ? (
                        <tr>
                            <td colSpan={8} style={{ textAlign: "center", color: "#666" }}>
                                No transactions in this period.
                            </td>
                        </tr>
                    ) : (
                        rows.map((r, i) => (
                            <tr key={`${r.reference}-${i}`} style={r.excludedReason ? { color: "#999" } : undefined}>
                                <td>{r.when}</td>
                                <td>{r.source}</td>
                                <td>{r.reference}</td>
                                <td>{r.description}</td>
                                <td style={{ textAlign: "right" }}>{money(r.gross)}</td>
                                <td style={{ textAlign: "right" }}>{r.refunded ? money(r.refunded) : ""}</td>
                                <td style={{ textAlign: "right" }}>
                                    {r.excludedReason ? "-" : <strong>{money(r.counted)}</strong>}
                                </td>
                                <td>{r.excludedReason || ""}</td>
                            </tr>
                        ))
                    )}
                </tbody>
            </table>
        </div>
    );
}
